import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IS_BROWSER } from '~/app/constants'
import { resetQuickViewId } from '~/app/store/debug/actions'
import { ThunkDispatch } from '~/app/store/interface'
import { tileEntityByIdSelector } from '~/app/store/tileEntity'

/**
 * Canonicalize a path-based (`/qv/<id>`) or Google-search (`?quickView=<id>`) entry
 * into `?qv=<id>`. Idempotent: no-ops when `?qv=` is already present, so it is safe to
 * call on every render. No-ops on the server (IS_BROWSER guard).
 */
const canonicalizeQuickViewParam = (designId: string): void => {
  if (!IS_BROWSER) {
    return
  }

  const url = new URL(window.location.href)
  if (url.searchParams.has('qv')) {
    return
  }

  // remove "/qv/${designId}"
  if (url.pathname.includes('/qv/')) {
    url.pathname = url.pathname.split('/qv/')[0]
  }

  // remove "?quickView=${designId}"
  if (url.searchParams.has('quickView')) {
    url.searchParams.delete('quickView')
  }

  // set QV to "path?qv=${designId}"
  url.searchParams.set('qv', designId)

  window.history.replaceState(null, '', url.toString())
}

export const useValidQuickViewEntity = (designId?: string | null) => {
  const dispatch = useDispatch<ThunkDispatch>()
  const tileEntityById = useSelector(tileEntityByIdSelector)
  const tileEntity = designId ? tileEntityById(designId) : null

  // Canonicalize `quickView`/path → `qv` SYNCHRONOUSLY during render, not in an effect.
  // The new QuickView package (`@vp/quick-view-modal`, urlQueryParam mode) reads `?qv=`
  // exactly once at mount to decide whether to open, and `replaceState` fires no
  // `popstate`. If we rewrote the URL in a post-commit effect, the package — which
  // mounts in the SAME commit as this hook's caller — would read the still-`?quickView=`
  // URL, set isOpen=false, and never re-open. Writing here ensures `?qv=` is present
  // before the package's mount-time read. The write is idempotent and SSR-safe, so
  // calling it on every render is safe.
  if (designId && tileEntity) {
    canonicalizeQuickViewParam(designId)
  }

  // Force-close when the id is not a known gallery tile entity. This dispatch cannot
  // run during render, so it stays in an effect.
  useEffect(() => {
    if (!designId) {
      return
    }

    if (!tileEntityById(designId)) {
      dispatch(resetQuickViewId())
    }
  }, [dispatch, designId, tileEntityById])

  return tileEntity ?? null
}
